/**
 * Form schemas (spec §6.7). Hand-written on purpose: the generated types say what the API accepts,
 * these say what a form may submit, which is stricter and carries the messages a user reads.
 *
 * The patterns mirror the backend's validation exactly — a form that lets a value through only to
 * have the API reject it is worse than no client validation at all.
 */

import { z } from 'zod';

import { EXPORT_SORTS, PLURAL_FORMS, TOKEN_SCOPES } from '../constants';

/** `hbh`, `rep`, `mga` — a project code is also its default key prefix (spec §3.2 rule 1). */
export const PROJECT_CODE_PATTERN = /^[a-z][a-z0-9-]{1,9}$/;
export const PACKAGE_CODE_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
/**
 * A project code is a folder in `locales/` (spec §8.4), and `packages` already names the package
 * side of every such path. Mirrors `RESERVED_PROJECT_CODES` in backend/app/modules/projects/schemas.py.
 */
export const RESERVED_PROJECT_CODES: readonly string[] = ['packages'];
/**
 * Prilog B: dot-separated segments, no leading/trailing dot, no `..`. Letters and digits of any script,
 * plus `_`, `-`, `&`, `@` and spaces inside the name — codes from backend data such as
 * `LocalCoverage.Kärnten`, `VehicleBrand.FE_O&K` or pct's `Company.GrECo Int. LT`. Only the plain
 * space, never at the start or end. Mirrors `KEY_NAME_PATTERN` in backend/app/lib/i18n/key_names.py.
 */
export const KEY_NAME_PATTERN = /^(?! )[\p{L}\p{N}_&@ -]+(\.[\p{L}\p{N}_&@ -]+)*(?<! )$/u;
const KEY_NAME_MESSAGE =
  'Letters, digits, underscore, dash, & and @, separated by dots; spaces only inside';
export const LOCALE_PATTERN = /^[A-Za-z]{2,3}(-[A-Za-z]{4})?(-[A-Za-z]{2}|-\d{3})?$/;

export const KEY_NAME_MAX_LENGTH = 400;
export const VALUE_MAX_LENGTH = 20_000;

const localeCode = z.string().regex(LOCALE_PATTERN, 'Not a valid BCP 47 locale code');

export const projectCreateSchema = z.object({
  code: z
    .string()
    .regex(PROJECT_CODE_PATTERN, 'Lower-case letters, digits and dashes; 2-10 characters')
    .refine((code) => !RESERVED_PROJECT_CODES.includes(code), 'This code is reserved'),
  name: z.string().min(1, 'Required').max(100),
  description: z.string().max(1000).optional(),
  sourceLocale: localeCode,
  locales: z.array(z.object({ locale: localeCode, fallbackLocale: localeCode.optional() })).min(1),
  keyPrefixes: z.array(z.string().min(1).max(100)).optional(),
  packages: z.array(z.string()).optional(),
});
export type ProjectCreateInput = z.input<typeof projectCreateSchema>;

/**
 * A shared package.
 *
 * Unlike a project, the prefixes are not derived from the code: a package exists to own names an
 * application already uses (`Common.`, `Country.`), so at least one has to be stated (§3.1).
 */
export const packageCreateSchema = z.object({
  code: z
    .string()
    .regex(PACKAGE_CODE_PATTERN, 'Lower-case letters, digits and dashes; 2-40 characters'),
  name: z.string().min(1, 'Required').max(100),
  description: z.string().max(1000).optional(),
  sourceLocale: localeCode,
  keyPrefixes: z.array(z.string().min(1).max(100)).min(1, 'At least one prefix'),
  locales: z.array(localeCode).optional(),
});
export type PackageCreateInput = z.input<typeof packageCreateSchema>;

/** Export behaviour and review policy; every field is optional because the form is a PATCH. */
export const projectSettingsSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(1000).optional(),
  sourceLocale: localeCode.optional(),
  exportIncludeEmpty: z.boolean().optional(),
  exportMachine: z.boolean().optional(),
  exportSort: z.enum(EXPORT_SORTS).optional(),
  exportGroupBlankLines: z.boolean().optional(),
  prodPublishTranslator: z.boolean().optional(),
  aiAutoTranslate: z.boolean().optional(),
  keyPrefixes: z.array(z.string().min(1).max(100)).optional(),
});
export type ProjectSettingsInput = z.input<typeof projectSettingsSchema>;

export const keyCreateSchema = z.object({
  name: z
    .string()
    .min(1, 'Required')
    .max(KEY_NAME_MAX_LENGTH)
    .regex(KEY_NAME_PATTERN, KEY_NAME_MESSAGE),
  description: z.string().max(2000).optional(),
  isPlural: z.boolean().default(false),
  maxLength: z.number().int().positive().max(VALUE_MAX_LENGTH).optional(),
  tags: z.array(z.string().min(1).max(60)).default([]),
  /** `{ "en-GB": "Save" }`, or one value per plural form for a plural key. */
  translations: z
    .record(
      localeCode,
      z.union([
        z.string().max(VALUE_MAX_LENGTH),
        z.record(z.enum(PLURAL_FORMS), z.string().max(VALUE_MAX_LENGTH)),
      ]),
    )
    .optional(),
});
export type KeyCreateInput = z.input<typeof keyCreateSchema>;

export const keyRenameSchema = z.object({
  newName: z
    .string()
    .min(1, 'Required')
    .max(KEY_NAME_MAX_LENGTH)
    .regex(KEY_NAME_PATTERN, KEY_NAME_MESSAGE),
  /** Keeps the old name resolvable for 30 days, so a deployed build does not break (Prilog B). */
  keepAlias: z.boolean().default(true),
});
export type KeyRenameInput = z.input<typeof keyRenameSchema>;

/**
 * How a project's target behaves. Where it writes is not part of it: the server derives the
 * environment, the account and the folder (`<container>/<project>/`, spec §8.4).
 */
export const targetCreateSchema = z.object({
  cacheControl: z.string().max(100).default('public, max-age=60'),
  cdnPurgeUrl: z.string().max(500).optional(),
  autoPublish: z.boolean().default(false),
  autoPublishDebounceS: z.number().int().min(0).max(3600).default(30),
  requiresReviewed: z.boolean().default(false),
  requiresQaClean: z.boolean().default(false),
});
export type TargetCreateInput = z.input<typeof targetCreateSchema>;

export const publishSchema = z.object({
  note: z.string().max(1000).optional(),
  locales: z.array(localeCode).optional(),
  /** Skips the reviewed and QA gates; administrators only (spec §7.9). */
  force: z.boolean().default(false),
});
export type PublishInput = z.input<typeof publishSchema>;

export const glossaryTermSchema = z.object({
  ownerType: z.enum(['global', 'project', 'package']).default('global'),
  ownerId: z.number().int().positive().optional(),
  term: z.string().min(1, 'Required').max(200),
  note: z.string().max(1000).optional(),
  doNotTranslate: z.boolean().default(false),
  translations: z.record(localeCode, z.string().min(1).max(400)).default({}),
});
export type GlossaryTermInput = z.input<typeof glossaryTermSchema>;

export const apiTokenSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  projectId: z.number().int().positive().optional(),
  scopes: z.array(z.enum(TOKEN_SCOPES)).min(1, 'Pick at least one scope'),
  expiresInDays: z.number().int().min(1).max(365).default(180),
});
export type ApiTokenInput = z.input<typeof apiTokenSchema>;

export const grantSchema = z.object({
  scopeType: z.enum(['project', 'package']),
  scopeId: z.number().int().positive(),
  /** Empty means every locale of the scope (spec §5.1); the API refuses a locale the scope lacks. */
  locales: z.array(localeCode).optional(),
});
export type GrantInput = z.input<typeof grantSchema>;
